/**
 * PATH: apps/qa/src/pages/area-execution/CreateNCRModal.tsx
 * PURPOSE: Create Non-Conformance Report modal from failed checklist items
 */

import { useState } from 'react'
import { X, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CreateNCRModalProps {
  isOpen: boolean; onClose: () => void
  onSubmit: (data: { title: string; description: string; severity: string }) => void
  itemTitle: string; isLoading: boolean
}

export function CreateNCRModal({ isOpen, onClose, onSubmit, itemTitle, isLoading }: CreateNCRModalProps) {
  const [title, setTitle] = useState(`NCR: ${itemTitle}`)
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium')

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />Create Non-Conformance
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Title <span className="text-destructive">*</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description <span className="text-destructive">*</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe the non-conformance..."
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Severity</label>
            <div className="grid grid-cols-4 gap-2">
              {(['low', 'medium', 'high', 'critical'] as const).map(s => (
                <button key={s} onClick={() => setSeverity(s)}
                  className={cn('py-2 rounded-lg text-sm font-medium transition-colors capitalize',
                    severity === s
                      ? s === 'low' ? 'bg-blue-500 text-white' : s === 'medium' ? 'bg-amber-500 text-white' : s === 'high' ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={() => onSubmit({ title, description, severity })} disabled={!title.trim() || !description.trim() || isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : <><AlertCircle className="w-4 h-4" />Create NCR</>}
          </button>
        </div>
      </div>
    </div>
  )
}
